import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Popper,
  PopperProps,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'

export type TValues = {
  id: number
  name: string
  color: string
  checked: boolean
}

interface IColoredMultySelect {
  valuesList: TValues[]
  isSquare: boolean
  onChangeSelected: (nextSelectedNames: string[]) => void
}

const ColoredAutoSelect = ({
  valuesList,
  isSquare,
  onChangeSelected,
}: IColoredMultySelect) => {
  const theme = useTheme()

  const getTextColor = (bg: string) => {
    try {
      return theme.palette.getContrastText(bg)
    } catch {
      return theme.palette.common.white
    }
  }

  const value = valuesList.filter((v) => v.checked)
  return (
    <Stack>
      <Autocomplete
        sx={{
          width: {
            xs: '150px',
            sm: '400px',
            md: '245px',
            lg: '340px',
            xl: '330px',
          },
        }}
        multiple
        id="tags-outlined"
        options={valuesList}
        value={value}
        onChange={(_, newValue) => {
          onChangeSelected(newValue.map((v) => v.name))
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disableCloseOnSelect
        disablePortal={false}
        ListboxProps={{ style: { maxHeight: 200 } }}
        slots={{
          // Prefer opening down; can still flip up if no space
          popper: (props) => <Popper {...props} placement="bottom-start" />,
        }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'flip',
                enabled: true,
                options: { fallbackPlacements: ['top-start'] },
              },
              {
                name: 'preventOverflow',
                options: {
                  altAxis: true,
                  rootBoundary: 'viewport',
                  padding: 8,
                },
              },
              { name: 'offset', options: { offset: [0, 6] } },
            ],
          } as Partial<PopperProps>,
          listbox: { style: { maxHeight: 290 } },
        }}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => {
            const chipBg = option.color
            const chipFg = getTextColor(chipBg)
            const { key: _chipKey, ...chipProps } = getTagProps({ index })
            return (
              <Chip
                key={option.id}
                {...chipProps}
                label={option.name}
                size="small"
                sx={{
                  bgcolor: chipBg,
                  color: chipFg,
                  '& .MuiChip-label': { px: 1 },
                }}
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: isSquare ? 0 : '50%',
                      bgcolor: chipFg,
                    }}
                  />
                }
              />
            )
          })
        }
        renderOption={(props, option, state) => {
          const { key, ...liProps } =
            props as React.HTMLAttributes<HTMLLIElement> & { key: string }
          return (
            <li key={key} {...liProps}>
              <Checkbox checked={state.selected} style={{ marginRight: 8 }} />
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: isSquare ? 0 : '50%',
                  backgroundColor: option.color,
                  marginRight: 1,
                }}
              />
              <Typography>{option.name}</Typography>
            </li>
          )
        }}
        renderInput={(params) => (
          <TextField {...params} label="select" placeholder="" />
        )}
      />
    </Stack>
  )
}

export default ColoredAutoSelect
